import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { QUERY_KEYS } from '@features/campaigns/shared/consts/queryKeys';
import { readCampaignDetails } from '@features/campaigns/shared/services/campaign/read';
import { CAMPAIGN_BUILDER_STEPS, PossibleStepNumber } from '@features/campaigns/shared/types/campaign';
import { CampaignDetailsQuery } from '@features/campaigns/shared/types/campaignDetails';
import { CampaignId, IncludeParams } from '@shared/types/campaign';

interface CampaignDetailsRequestParams {
  campaignId: CampaignId;
  campaignStep?: PossibleStepNumber;
  enabled?: boolean;
}

export default function useCampaignDetails({ campaignId, campaignStep, enabled }: CampaignDetailsRequestParams) {
  const { pathname } = useLocation();
  const isCampaignDetailsView = pathname.includes('details') && !pathname.includes('builder');

  const campaignDetailsQuery = useMemo<CampaignDetailsQuery>(() => {
    const shouldIncludePricing =
      isCampaignDetailsView ||
      campaignStep === CAMPAIGN_BUILDER_STEPS.STEP_04 ||
      campaignStep === CAMPAIGN_BUILDER_STEPS.STEP_05;

    return { include: shouldIncludePricing ? IncludeParams.PRICING : undefined };
  }, [campaignStep, isCampaignDetailsView]);

  return useQuery({
    queryKey: [QUERY_KEYS.CAMPAIGN_DETAILS, campaignId, campaignStep, campaignDetailsQuery],
    queryFn: () => readCampaignDetails(campaignId, campaignDetailsQuery),
    gcTime: 0,
    enabled,
  });
}
